import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from './Button';

export interface FileUploaderProps {
  label?: string;
  accept?: Record<string, string[]>;
  maxSizeBytes?: number;
  multiple?: boolean;
  disabled?: boolean;
  helperText?: string;
  className?: string;
  compact?: boolean;
  onFilesSelected?: (files: File[]) => void;
  onError?: (message: string) => void;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  label,
  accept,
  maxSizeBytes,
  multiple = false,
  disabled = false,
  helperText,
  className = '',
  compact = false,
  onFilesSelected,
  onError,
}) => {
  const [isBusy, setIsBusy] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const maxSize = maxSizeBytes ?? Infinity;
  const isMultiple = false;  

  const allowedDescription = useMemo(() => {
    if (!accept) return '';
    const extensions = Object.values(accept).flat();
    if (extensions.length > 0) return extensions.join(', ');
    const mimeTypes = Object.keys(accept);
    return mimeTypes.join(', ');
  }, [accept]);

  const isFileTypeAllowed = useCallback(
    (file: File) => {
      if (!accept || Object.keys(accept).length === 0) return true;

      const mimeTypes = Object.keys(accept);
      const extensions = Object.values(accept).flat().map((e) => e.toLowerCase());

      const fileMime = file.type;
      const fileExt = file.name.includes('.')
        ? `.${file.name.split('.').pop()!.toLowerCase()}`
        : '';

      const mimeOk = !!fileMime && mimeTypes.some((m) => {
        if (m.endsWith('/*')) {
          const prefix = m.slice(0, -1); // keep the '/'
          return fileMime.startsWith(prefix);
        }
        return fileMime === m;
      });

      const extOk = !!fileExt && extensions.includes(fileExt);

      return mimeOk || extOk;
    },
    [accept]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: any[]) => {
      if (disabled) return;
      if (fileRejections && fileRejections.length > 0) {
        onError?.(
          `The selected file does not meet the upload constraints${allowedDescription ? ` (allowed: ${allowedDescription})` : ''}.`
        );
        return;
      }
      if (acceptedFiles.some((f) => f.size > maxSize)) {
        onError?.('One or more files exceed the maximum allowed size.');
        return;
      }
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (!isFileTypeAllowed(file)) {
          onError?.(
            `Selected file type is not allowed${allowedDescription ? ` (allowed: ${allowedDescription})` : ''}.`
          );
          return;
        }
      }
      setIsBusy(true);
      try {
        setSelectedFiles(acceptedFiles);
        onFilesSelected?.(acceptedFiles);
      } finally {
        setIsBusy(false);
      }
    },
    [disabled, maxSize, onFilesSelected, onError, isFileTypeAllowed, allowedDescription]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept,
    multiple: isMultiple,
    disabled,
    noClick: true,
    noKeyboard: true,
  });

  const borderClass = useMemo(() => {
    if (disabled) return 'border-secondary-200 bg-secondary-50 cursor-not-allowed';
    if (isDragActive) return 'border-blue-400 bg-blue-50';
    return 'border-secondary-300 bg-white hover:border-secondary-400';
  }, [disabled, isDragActive]);

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className={`block ${compact ? 'text-xs' : 'text-sm'} font-medium text-secondary-700 ${compact ? 'mb-1' : 'mb-2'}`}>{label}</label>
      )}
      <div
        {...getRootProps()}
        className={`flex flex-col items-center justify-center ${compact ? 'p-3' : 'p-6'} border-2 border-dashed rounded-md transition-colors ${borderClass}`}
      >
        <input {...getInputProps()} />
        <svg className={`${compact ? 'w-7 h-7' : 'w-10 h-10'} text-secondary-400`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6H16a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p className={`${compact ? 'mt-2 text-xs' : 'mt-3 text-sm'} text-secondary-600 text-center`}>
          {isDragActive ? 'Drop files here…' : 'Drag & drop files here, or use the button to select'}
        </p>
        {helperText && (
          <p className={`${compact ? 'mt-1 text-[11px]' : 'mt-1 text-xs'} text-secondary-500 text-center`}>{helperText}</p>
        )}
        <div className={compact ? 'mt-2' : 'mt-4'}>
          <Button variant="outline" onClick={open} disabled={disabled || isBusy}>
            {isBusy ? 'Processing…' : isMultiple ? 'Select Files' : 'Select File'}
          </Button>
        </div>
        {selectedFiles.length > 0 && (
          <div className={`${compact ? 'mt-2 text-xs' : 'mt-3 text-sm'} text-secondary-700 text-center`}>
            <span className="font-medium">Selected:</span>{' '}
            {isMultiple
              ? selectedFiles.map((f) => f.name).join(', ')
              : selectedFiles[0]?.name}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploader;


